/**
 * Primitives for narrowing an `unknown` HTTP body into typed values.
 *
 * The repo carries no schema-validation library and the project rule is explicit:
 * data from the API is `unknown` plus validation, never `any` and never a cast.
 * These seven readers are the shared floor every feature parser stands on
 * (`features/my-courses/my-course.parser.ts` was the first, S-03 the second) —
 * one copy, so an edge case fixed once stays fixed everywhere.
 *
 * What is NOT here: anything that knows a field name, a domain shape or an error
 * message. Envelope-vs-item failure policy belongs to the feature parser, because
 * it is a product decision (a broken envelope throws, a broken item is dropped),
 * not a type-narrowing one.
 *
 * Property access goes through `['key']` because `noPropertyAccessFromIndexSignature`
 * is on.
 */
export type UnknownRecord = Record<string, unknown>;

export function asRecord(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

export function readString(source: UnknownRecord, key: string): string | null {
  const value = source[key];

  return typeof value === 'string' ? value : null;
}

/** Like readString, but a whitespace-only value is "absent", not a value. */
export function readNonBlankString(source: UnknownRecord, key: string): string | null {
  const value = readString(source, key);

  return value !== null && value.trim() !== '' ? value : null;
}

export function readNumber(source: UnknownRecord, key: string): number | null {
  const value = source[key];

  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function readBoolean(source: UnknownRecord, key: string): boolean {
  return source[key] === true;
}

/** Dates travel as ISO strings (JSON has no Date); anything unparseable is `null`. */
export function readDate(source: UnknownRecord, key: string): Date | null {
  const value = source[key];

  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function readStringArray(source: UnknownRecord, key: string): string[] {
  const value = source[key];

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry): entry is string => typeof entry === 'string');
}
